import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Checkbox, Label, Spinner, TextInput } from "flowbite-react";
import { Chart } from "react-google-charts";
import { toast } from "react-toastify";
import AwardVotingApi, { AwardResults } from "../../Api/AwardVotingApi";
import CommonApi from "../../Api/CommonApi";
import { Subscription, SubscriptionLevelNames } from "../../Models/Subscription";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";

function toLocalInputValue(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local: string): string | null {
  if (!local?.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function escapeCsv(value: string) {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

const emptyForm = () => ({
  id: 0,
  year: new Date().getFullYear(),
  title: `EMDA Awards ${new Date().getFullYear()}`,
  startsUtc: toLocalInputValue(new Date().toISOString()),
  endsUtc: toLocalInputValue(new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString()),
  isPublished: false,
  showWinnersToMembers: false,
  subscriptionIds: [] as number[],
});

const AdminAwardsEditPage: React.FC = () => {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);
  const isNew = !periodId;
  const numericId = Number(periodId);

  const [form, setForm] = useState(emptyForm());
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [results, setResults] = useState<AwardResults | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      navigate("/");
    }
  }, [userModel, navigate]);

  useEffect(() => {
    CommonApi.getSubscriptions()
      .then((res) => {
        const active = (res.data ?? []).filter(
          (s) =>
            s.id !== SubscriptionLevelNames.Deactivated &&
            s.id !== SubscriptionLevelNames.Rejected
        );
        setSubscriptions(active);
      })
      .catch(() => toast.error("Unable to load membership types."));
  }, []);

  useEffect(() => {
    if (isNew || !Number.isFinite(numericId) || numericId <= 0) return;

    let active = true;
    setLoading(true);
    Promise.all([
      AwardVotingApi.getPeriods(),
      AwardVotingApi.getResults(numericId),
    ])
      .then(([periodsRes, resultsRes]) => {
        if (!active) return;
        const period = (periodsRes.data ?? []).find((p) => p.id === numericId);
        if (!period) {
          toast.error("Voting period not found.");
          navigate("/admin/awards");
          return;
        }
        setForm({
          id: period.id,
          year: period.year,
          title: period.title,
          startsUtc: toLocalInputValue(period.startsUtc),
          endsUtc: toLocalInputValue(period.endsUtc),
          isPublished: period.isPublished,
          showWinnersToMembers: period.showWinnersToMembers,
          subscriptionIds: period.subscriptionIds?.length
            ? period.subscriptionIds
            : period.subscriptionId
              ? [period.subscriptionId]
              : [],
        });
        setResults(resultsRes.data);
      })
      .catch(() => {
        if (active) toast.error("Unable to load voting period.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isNew, numericId, navigate]);

  const save = async () => {
    const starts = fromLocalInputValue(form.startsUtc);
    const ends = fromLocalInputValue(form.endsUtc);
    if (!starts || !ends) {
      toast.error("Start and end dates are required.");
      return;
    }
    const subscriptionIds = form.subscriptionIds.filter((id) => id > 0);
    if (subscriptionIds.length === 0) {
      toast.error("Choose at least one membership type.");
      return;
    }

    setSaving(true);
    try {
      const res = await AwardVotingApi.upsertPeriod({
        id: form.id,
        year: Number(form.year),
        title: form.title,
        startsUtc: starts,
        endsUtc: ends,
        isPublished: form.isPublished,
        showWinnersToMembers: form.showWinnersToMembers,
        subscriptionId: subscriptionIds[0],
        subscriptionIds,
      });
      if (!res.data.success) {
        toast.error(res.data.message || "Unable to save period.");
        return;
      }
      toast.success("Voting period saved.");
      const id = Number(res.data.affectedEntityIdentifier || form.id);
      if (isNew && id) {
        navigate(`/admin/awards/${id}`, { replace: true });
      } else if (id) {
        const resultsRes = await AwardVotingApi.getResults(id);
        setResults(resultsRes.data);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax?.response?.data?.message || "Unable to save period.");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    if (!results) return;
    const rows = [
      ["Person", "Company", "VoteCount"],
      ...results.tallies.map((t) => [
        escapeCsv(t.name),
        escapeCsv(t.memberName),
        String(t.voteCount),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `awards_${results.period.year}_results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner size="xl" />
      </div>
    );
  }

  const chartRows = results?.tallies ?? [];
  const chartLabel = (row: (typeof chartRows)[number]) =>
    row.userId > 0 && row.memberName && row.memberName !== row.name
      ? `${row.name} — ${row.memberName}`
      : row.name || row.memberName;
  const chartData = [
    ["Person", "Votes"],
    ...chartRows.map((t) => [chartLabel(t), t.voteCount]),
  ];

  return (
    <div className="w-full p-4 md:p-6 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => navigate("/admin/awards")}
          >
            Back to periods
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isNew ? "New voting period" : "Edit voting period"}
          </h1>
        </div>
      </div>

      <section className="max-w-2xl space-y-4 rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <Label htmlFor="award-year">Year</Label>
          <TextInput
            id="award-year"
            type="number"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
          />
        </div>
        <div>
          <Label htmlFor="award-title">Title</Label>
          <TextInput
            id="award-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>
        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">Membership types</p>
          <div className="space-y-2">
            {subscriptions.map((s) => {
              const checked = form.subscriptionIds.includes(s.id);
              return (
                <label key={s.id} className="flex items-center gap-2" htmlFor={`award-subscription-${s.id}`}>
                  <Checkbox
                    id={`award-subscription-${s.id}`}
                    checked={checked}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        subscriptionIds: e.target.checked
                          ? [...form.subscriptionIds, s.id]
                          : form.subscriptionIds.filter((id) => id !== s.id),
                      })
                    }
                  />
                  <span>{s.name}</span>
                </label>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Voters choose any person on an approved member in the selected types. One ballot can cover Distributors and Reps, or Associates and Manufacturers.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="award-start">Starts</Label>
            <TextInput
              id="award-start"
              type="datetime-local"
              value={form.startsUtc}
              onChange={(e) => setForm({ ...form, startsUtc: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="award-end">Ends</Label>
            <TextInput
              id="award-end"
              type="datetime-local"
              value={form.endsUtc}
              onChange={(e) => setForm({ ...form, endsUtc: e.target.value })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="award-published"
            checked={form.isPublished}
            onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
          />
          <Label htmlFor="award-published">Published</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="award-winners"
            checked={form.showWinnersToMembers}
            onChange={(e) => setForm({ ...form, showWinnersToMembers: e.target.checked })}
          />
          <Label htmlFor="award-winners">Show winners to members after voting closes</Label>
        </div>
        <Button className="buttonMain" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save period"}
        </Button>
      </section>

      {!isNew && results && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Results ({results.totalVoters} unique {results.totalVoters === 1 ? "voter" : "voters"})
            </h2>
            <Button color="light" onClick={exportCsv} disabled={chartRows.length === 0}>
              Export CSV
            </Button>
          </div>
          {chartRows.length === 0 ? (
            <p className="text-sm text-gray-500">No votes yet.</p>
          ) : (
            <>
              <div className="rounded-lg border border-gray-200 bg-white p-2">
                <Chart
                  chartType="BarChart"
                  data={chartData}
                  options={{
                    legend: { position: "none" },
                    hAxis: { minValue: 0, format: "0" },
                    chartArea: { left: 240, top: 16, width: "62%", height: "80%" },
                  }}
                  width="100%"
                  height={`${Math.max(280, chartRows.length * 40)}px`}
                />
              </div>
              <div className="overflow-auto border rounded">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2">Person</th>
                      <th className="text-left px-3 py-2">Company</th>
                      <th className="text-left px-3 py-2">Votes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartRows.map((row) => (
                      <tr key={`${row.userId}-${row.memberId}`} className="border-t">
                        <td className="px-3 py-2">{row.name}</td>
                        <td className="px-3 py-2">{row.memberName}</td>
                        <td className="px-3 py-2 font-semibold">{row.voteCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default AdminAwardsEditPage;
